import { normalizeMapObjectPlacement } from './mapObjectPlacement';
import { isSyntheticTiledMapObjectAssetId } from './syntheticTileObjects';
import type { MapObjectPlacement } from '../model/gamestate/types';
import type { MapObjectPlacementRecord } from '../spacetime/types';

export interface CanonicalMapObjectRecord {
  readonly id: string;
  readonly asset?: string;
  readonly x: number;
  readonly y: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly sizeMultiplier: number;
  readonly flipHorizontal: boolean;
  readonly flipVertical: boolean;
  readonly rotationSteps: number;
  readonly layer?: string;
}

function readFiniteNumber(value: unknown, fallback: number): number {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function coerceAuthoritativeMapObjectRecord(
  record: MapObjectPlacementRecord,
): MapObjectPlacementRecord {
  const normalized = normalizeMapObjectPlacement({
    x: readFiniteNumber(record.x, 0),
    y: readFiniteNumber(record.y, 0),
    offsetX: readFiniteNumber(record.offsetX, 0),
    offsetY: readFiniteNumber(record.offsetY, 0),
    sizeMultiplier: readFiniteNumber(record.sizeMultiplier, 1),
  });
  return {
    id: record.id,
    asset: record.asset,
    x: normalized.x,
    y: normalized.y,
    offsetX: normalized.offsetX,
    offsetY: normalized.offsetY,
    sizeMultiplier: normalized.sizeMultiplier,
    flipHorizontal: record.flipHorizontal === true,
    flipVertical: record.flipVertical === true,
    rotationSteps: readFiniteNumber(record.rotationSteps, 0),
    layer: record.layer,
    sortOrder: readFiniteNumber(record.sortOrder, 0),
  };
}

export function parsePersistedMapObjects(
  objectsRaw: unknown,
  width: number,
  height: number,
): MapObjectPlacement[] {
  if (!Array.isArray(objectsRaw)) {
    return [];
  }

  return objectsRaw
    .filter((entry): entry is MapObjectPlacementRecord =>
      typeof entry === 'object'
      && entry !== null
      && typeof (entry as Record<string, unknown>).id === 'string'
      && (typeof (entry as Record<string, unknown>).x === 'number'
        || Number.isFinite(Number((entry as Record<string, unknown>).x)))
      && (typeof (entry as Record<string, unknown>).y === 'number'
        || Number.isFinite(Number((entry as Record<string, unknown>).y)))
    )
    .filter((entry) => !isSyntheticTiledMapObjectAssetId(typeof entry.asset === 'string' ? entry.asset : entry.id))
    .map((entry) => {
      const normalized = coerceAuthoritativeMapObjectRecord(entry);
      return {
        id: normalized.id,
        asset: typeof normalized.asset === 'string' ? normalized.asset : normalized.id,
        x: Math.max(0, Math.min(width - 1, Math.floor(normalized.x))),
        y: Math.max(0, Math.min(height - 1, Math.floor(normalized.y))),
        offsetX: normalized.offsetX,
        offsetY: normalized.offsetY,
        sizeMultiplier: normalized.sizeMultiplier,
        flipHorizontal: normalized.flipHorizontal === true,
        flipVertical: normalized.flipVertical === true,
        rotationSteps: Math.trunc(readFiniteNumber(normalized.rotationSteps, 0)),
        layer: typeof normalized.layer === 'string' ? normalized.layer : undefined,
      };
    });
}

export function buildCanonicalMapObjectRecords(
  objects: readonly MapObjectPlacementRecord[],
): CanonicalMapObjectRecord[] {
  return objects
    .filter((object) => !isSyntheticTiledMapObjectAssetId(object.asset ?? object.id))
    .map((object) => {
      const normalized = coerceAuthoritativeMapObjectRecord(object);
      return {
        id: normalized.id,
        asset: normalized.asset,
        x: normalized.x,
        y: normalized.y,
        offsetX: normalized.offsetX ?? 0,
        offsetY: normalized.offsetY ?? 0,
        sizeMultiplier: normalized.sizeMultiplier ?? 1,
        flipHorizontal: normalized.flipHorizontal === true,
        flipVertical: normalized.flipVertical === true,
        rotationSteps: normalized.rotationSteps ?? 0,
        layer: normalized.layer,
      };
    });
}
